<script>
import Card from '@/components/common/Card.vue';
import QRCodeGenerator from '@/components/common/QRCodeGenerator.vue';
import { TwoStepVerification } from '@/api/api';
import TwoStepVerificationDeactivateForm from './TwoStepVerificationDeactivateForm.vue';

export default {
  name: 'TwoStepVerificationActivateForm',
  components: { Card, QRCodeGenerator, TwoStepVerificationDeactivateForm },
  data() {
    return {
      activationStep: 1,
      isLoading: true,
      code: '',
      setupURI: '',
      showDeactivateForm: false,
      errorMessage: '',
    };
  },
  created() {
    this.getStatus();
  },
  methods: {
    async getStatus() {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        const { otpEnabled } = await TwoStepVerification.getStatus();
        if (otpEnabled) this.activationStep = 3;
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },
    async initiate() {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        const { uri } = await TwoStepVerification.setup();
        this.setupURI = uri;
        this.activationStep = 2;
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },
    async submitCode() {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        await TwoStepVerification.enable(this.code);
        this.activationStep = 3;
        this.code = '';
        this.uri = '';
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },
    async deactivate(otp) {
      try {
        this.isLoading = true;
        this.errorMessage = '';
        await TwoStepVerification.disable(otp);
        this.showDeactivateForm = false;
        this.activationStep = 1;
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    },
    cancelDeactivate() {
      this.showDeactivateForm = false;
      this.errorMessage = '';
    },
  },
};
</script>

<template>
  <Transition>
    <Card v-loading="isLoading" class="two-step-verification-activate-form">
      <div v-if="activationStep === 1">
        <ElAlert type="warning" show-icon :closable="false">
          {{ $t('SETTINGS.TWO_STEP_VERIFICATION.STATUS_INACTIVE') }}
        </ElAlert>

        <div class="two-step-verification-activate-form__intro">
          <div>
            <p>
              {{ $t('SETTINGS.TWO_STEP_VERIFICATION.RECOMMEND_TO_ACTIVE') }}
            </p>

            <ElButton
              icon="el-icon-s-tools"
              type="primary"
              class="two-step-verification-activate-form__button"
              @click="initiate"
            >
              {{ $t('SETTINGS.TWO_STEP_VERIFICATION.ACTIVATE') }}
            </ElButton>
          </div>

          <img
            class="two-step-verification-activate-form__image"
            :src="require('@/assets/images/two-step-verification.svg')"
            width="400"
            alt=""
          />
        </div>
      </div>

      <div
        v-else-if="activationStep === 2"
        class="two-step-verification-activate-form__initiate"
      >
        <div>
          <p class="text-bold">
            {{ $t('SETTINGS.TWO_STEP_VERIFICATION.ACTIVATE') }}
          </p>
          <div
            v-dompurify-html="
              $t('SETTINGS.TWO_STEP_VERIFICATION.ACTIVATE_GUIDE')
            "
          />
        </div>
        <div class="two-step-verification-activate-form__initiate-form">
          <QRCodeGenerator :content="setupURI" :size="200" />

          <ElInput
            v-model="code"
            type="code"
            dir="ltr"
            name="code"
            autofocus
            maxlength="6"
            class="text-center"
            autocomplete="off"
            placeholder="# # #   # # #"
            @keypress.enter.native="submitCode"
          />

          <ElButton type="primary" :disabled="!code" @click="submitCode">
            {{ $t('SETTINGS.TWO_STEP_VERIFICATION.CHECK_CODE') }}
          </ElButton>
        </div>
      </div>

      <div
        v-else-if="activationStep === 3"
        class="two-step-verification-activate-form__done"
      >
        <ElAlert type="success" show-icon :closable="false">
          {{ $t('SETTINGS.TWO_STEP_VERIFICATION.STATUS_ACTIVE') }}
        </ElAlert>

        <hr class="divider" />

        <ElButton
          v-show="!showDeactivateForm"
          size="small"
          icon="el-icon-unlock"
          type="text"
          class="text-danger text-hover-danger"
          @click="showDeactivateForm = true"
        >
          {{ $t('SETTINGS.TWO_STEP_VERIFICATION.DEACTIVATE') }}
        </ElButton>

        <TwoStepVerificationDeactivateForm
          v-show="showDeactivateForm"
          @confirm-click="deactivate"
          @cancel-click="cancelDeactivate"
        />
      </div>

      <div
        v-show="errorMessage"
        class="two-step-verification-activate-form__error text-danger text-small"
      >
        {{ errorMessage }}
      </div>
    </Card>
  </Transition>
</template>

<style lang="scss">
.two-step-verification-activate-form {
  &__intro {
    display: flex;
    gap: 3rem;
    align-items: center;
    margin-top: 2rem;

    @include breakpoint(medium down) {
      flex-wrap: wrap-reverse;
      justify-content: center;
    }
  }

  &__image {
    width: 40%;
  }

  &__button {
    width: 100%;
  }

  &__initiate {
    display: flex;
    gap: 2rem;

    @include breakpoint(medium down) {
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  &__initiate-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__done {
    .divider {
      margin: 1rem 0;
    }
  }

  &__error {
    margin-top: 1rem;
  }
}
</style>
